import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import * as moment from 'moment'
import { interval, startWith } from 'rxjs'
import { EventStatus } from '../../enums/event.enum'
import { Event } from '../../types/event.type'

type DateParts = {
  weekday: string
  day: string
  monthYear: string
}

@Component({
  selector: 'app-event-dates-status',
  standalone: true,
  imports: [NgClass],
  templateUrl: './event-dates-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventDatesStatusComponent {
  public readonly event = input.required<Event>()

  protected readonly EventStatus = EventStatus
  private readonly _momentFactory = ((moment as unknown as { default?: typeof moment }).default ?? (moment as unknown as typeof moment)) as typeof moment
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _timerTick = toSignal(interval(1000).pipe(startWith(0), takeUntilDestroyed(this._destroyRef)), { initialValue: 0 })

  protected readonly status = computed((): EventStatus => {
    this._timerTick()
    if (this.event().is_archived) return EventStatus.ARCHIVED

    if (this.isIndefinitelyEvent()) {
      const start = this._convertPublicEventDateFormat(this.event().public_start_date)
      return this._nowInEventOffset().isBefore(start) ? EventStatus.UPCOMING : EventStatus.LIVE
    }

    const start = this._convertPublicEventDateFormat(this.event().public_start_date)
    const end = this._convertPublicEventDateFormat(this.event().public_end_date)
    if (!start) return EventStatus.FINISHED

    const now = this._nowInEventOffset()
    if (!end) {
      if (now.isBefore(start)) return EventStatus.UPCOMING
      return EventStatus.LIVE
    }

    if (now.isBefore(start)) return EventStatus.UPCOMING
    if (now.isSameOrBefore(end)) return EventStatus.LIVE
    return EventStatus.FINISHED
  })

  /** UPCOMING → countdown to start; LIVE → elapsed since start (including open-ended). */
  protected readonly showTimer = computed(() => this.status() === EventStatus.UPCOMING || this.status() === EventStatus.LIVE)

  /** Event is marked as infinite by backend, independent from end-date presence. */
  protected readonly isIndefinitelyEvent = computed(() => Boolean(this.event().is_indefinitely) || Boolean(!this.event().public_end_date))

  /**
   * Countdown to start (UPCOMING) or elapsed since start (LIVE); minute precision only.
   */
  protected readonly timerText = computed((): string => {
    this._timerTick()
    const start = this._convertPublicEventDateFormat(this.event().public_start_date)
    if (!start) return ''

    const now = this._nowInEventOffset()
    if (this.status() === EventStatus.UPCOMING) return this._formatTimerDuration(start.diff(now))
    if (this.status() === EventStatus.LIVE) return this._formatTimerDuration(now.diff(start))
    return ''
  })

  protected readonly startParts = computed((): DateParts | null => this._convertPublicEventDateToParts(this.event().public_start_date))

  protected readonly endParts = computed((): DateParts | null => this._convertPublicEventDateToParts(this.event().public_end_date))

  /**
   * Converts a public event date string to date parts.
   * @param {string} raw - The public event date string to convert.
   * @returns {DateParts | null} The date parts, or null if the date string is invalid.
   */
  private _convertPublicEventDateToParts(raw: string): DateParts | null {
    if (!this._convertPublicEventDateFormat(raw)) return null
    return {
      weekday: this._convertPublicEventDateFormat(raw).format('ddd'),
      day: this._convertPublicEventDateFormat(raw).format('D'),
      monthYear: this._convertPublicEventDateFormat(raw).format('MMM YYYY')
    }
  }

  /**
   * Tries to parse a public event date string into a Moment object.
   * @param {string} dateString - The date string to parse.
   * @returns {Moment | null} The parsed Moment object, or null if the date string is invalid.
   */
  private _convertPublicEventDateFormat(dateString: string): moment.Moment | null {
    if (!dateString?.trim()) return null
    const raw = dateString.trim()
    const normalizedOffset = this._normalizedOffsetString()

    //TODO: Remove after integration with the new get events API
    // 1) Legacy timeline format from list API: "YYYY-MM-DD HH:mm:ss"
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
      if (normalizedOffset) {
        const withOffset = this._momentFactory.parseZone(`${raw}${normalizedOffset}`, 'YYYY-MM-DD HH:mm:ssZ', true)
        return withOffset.isValid() ? withOffset : null
      }
      const parsed = this._momentFactory(raw, 'YYYY-MM-DD HH:mm:ss', true)
      return parsed.isValid() ? parsed : null
    }

    // 2) ISO format from single-event APIs: "2026-05-30T17:00:00.000000Z".
    // represent the event's local time, not true UTC. Reinterpret them in the
    // event's offset so comparisons against `now` reflect the real instant.
    const utcParsed = this._momentFactory.utc(raw, moment.ISO_8601, true)
    if (!utcParsed.isValid()) return null
    if (!normalizedOffset) return utcParsed

    const wallClock = utcParsed.format('YYYY-MM-DDTHH:mm:ss')
    const local = this._momentFactory.parseZone(`${wallClock}${normalizedOffset}`, 'YYYY-MM-DDTHH:mm:ssZ', true)
    return local.isValid() ? local : null
  }

  /**
   * Returns "now" in the same fixed UTC offset used for event date parsing.
   * @returns {moment.Moment} The "now" in the same fixed UTC offset used for event date parsing.
   */
  private _nowInEventOffset(): moment.Moment {
    const normalizedOffset = this._normalizedOffsetString()
    if (!normalizedOffset) return this._momentFactory()
    return this._momentFactory().utcOffset(normalizedOffset)
  }

  /**
   * Normalizes offset from API (e.g. "+2", "-5", "+02:00") to a Moment-compatible value.
   * @returns {string | null} The normalized offset string, or null if the offset is invalid.
   */
  private _normalizedOffsetString(): string | null {
    const offsetValue = this.event().timezone_offset_string
    if (offsetValue === null || offsetValue === undefined) return null
    const rawOffset = String(offsetValue).trim()
    if (!rawOffset) return null

    if (/^[+-]\d{2}:\d{2}$/.test(rawOffset)) return rawOffset
    const hourOnly = rawOffset.match(/^([+-])(\d{1,2})$/)
    if (!hourOnly) return null
    const [, sign, hour] = hourOnly
    return `${sign}${hour.padStart(2, '0')}:00`
  }

  /**
   * Formats a duration in milliseconds to a compact duration string.
   * @param {number} ms - The duration in milliseconds.
   * @returns {string} The compact duration string.
   */
  private _formatTimerDuration(ms: number): string {
    const capped = Math.max(0, ms)
    const days = Math.floor(moment.duration(capped).asDays())
    const remainderMs = capped - days * 86400000
    const remainder = moment.duration(remainderMs)
    const hours = remainder.hours()
    const mins = remainder.minutes()
    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    if (mins > 0) parts.push(`${mins}m`)

    if (!parts.length) return capped > 0 ? '1m' : '0m'
    return parts.join(' ')
  }
}
